/* =========================================================================
 *  FM connector kit — quantity-led buy column with computed connectors
 *  -------------------------------------------------------------------
 *  Three plain questions — how many, what shape is the area, and how should
 *  they lock together — turned into a priced order and one multi-line add to
 *  basket. Replaces the variant dropdown on
 *  `templates/product.connector-kit.json`.
 *
 *  Reads the parts model emitted by `snippets/fm-connector-kit.liquid`. Prices
 *  are the resolved variants' own `variant.price` in pence; nothing here
 *  knows a price, and the design prototype's figures deliberately did not
 *  survive into the code.
 *
 *  One component, three surfaces
 *    desktop  steps + summary + CTA in the buy column.
 *    sheet    the same component inside the mobile bottom sheet, with the
 *             total and CTA pinned to its footer.
 *    buybar   the mobile closed-state footer: slim until the shopper has
 *             answered, then summary + Add all to Basket.
 *  `renderControls()` builds all of them from one code path — the round-two
 *  options-sheet lesson was that two render paths for one decision drift
 *  within a week.
 * ========================================================================= */
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 767;

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  /* Thousands-separated, to match Shopify's `money` filter. Fifty units is a
     four-figure order routinely, and "£4467.50" beside the page's own
     "£4,467.50" reads as a second pricing engine. */
  function money(pence) {
    var v = (pence / 100).toFixed(2);
    var parts = v.split('.');
    return '£' + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
  }

  /* Page VAT mode. fm-vat.js owns it (the Google Shopping `?incl_vat=true`
     rule, gclid, the manual toggle); the URL test is only the fallback for the
     moment before that script has run. */
  function vatInc() {
    if (window.fmVatMode) return window.fmVatMode === 'inc';
    return /[?&]incl_vat=true/i.test(window.location.search);
  }

  /* DISPLAY money — follows the page's VAT mode. The CTA deliberately does
     not: the sitewide rule is that headline prices flip and the basket button
     stays ex VAT. */
  function dispMoney(pence) {
    return money(vatInc() ? Math.round(pence * 1.2) : pence);
  }

  function vatSuffix() {
    return vatInc() ? 'Inc. VAT' : '+ VAT';
  }

  /* ------------------------------------------------------------------ *
   *  Tiny DOM builder — same contract as fm-tile-kit.js
   * ------------------------------------------------------------------ */
  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        var v = attrs[k];
        if (v === null || v === undefined || v === false) return;
        if (k === 'text') { n.textContent = v; return; }
        if (k === 'on') {
          Object.keys(v).forEach(function (ev) { n.addEventListener(ev, v[ev]); });
          return;
        }
        if (v === true) { n.setAttribute(k, ''); return; }
        n.setAttribute(k, v);
      });
    }
    append(n, kids);
    return n;
  }

  function append(parent, kids) {
    if (!kids) return parent;
    (Array.isArray(kids) ? kids : [kids]).forEach(function (k) {
      if (k === null || k === undefined || k === false) return;
      parent.appendChild(typeof k === 'string' ? document.createTextNode(k) : k);
    });
    return parent;
  }

  function clear(node) {
    while (node && node.firstChild) node.removeChild(node.firstChild);
  }

  document.addEventListener('DOMContentLoaded', function () {
    var dataNode = document.querySelector('[data-fm-ck-data]');
    if (!dataNode) return;

    var DATA;
    try {
      DATA = JSON.parse(dataNode.textContent);
    } catch (e) {
      return;
    }

    var controlsMount = document.querySelector('[data-fm-ck-controls]');
    if (!controlsMount) return;

    var sheet = document.querySelector('[data-fm-ck-sheet]');
    var sheetBody = document.querySelector('[data-fm-ck-sheet-body]');
    var sheetFooter = document.querySelector('[data-fm-ck-sheet-footer]');
    var bar = document.querySelector('[data-fm-ck-buybar]');

    var PARTS = DATA.parts || [];
    var MAT = PARTS.filter(function (p) { return p.role === 'mat'; })[0] || null;
    var CONNECTORS = PARTS.filter(function (p) { return p.role === 'connector'; });
    var LOCKS = DATA.locks || [];

    /* ---------------------------------------------------------------- *
     *  Tile size
     *  ---------
     *  One template serves a family of these products, and a template's
     *  settings are shared by every product assigned to it — so a typed
     *  915 × 2400 is right for exactly one of them. Heavy-Duty Ground
     *  Protection Tiles is 120 × 240cm.
     *
     *  So the size is read off the tile's own variant title, which carries it
     *  on both products, in different units:
     *      "Single Tile - 915mm x 2400mm x 10mm"
     *      "Single Tile - 120cm x 240cm x 12.7mm"
     *  The section settings override when they are set, for a product whose
     *  title does not.
     *
     *  Blast radius if this parses wrong is small and worth knowing: the size
     *  feeds the "Each tile is …" line and the coverage figure ONLY. Connector
     *  counts and every price are independent of it. It still refuses anything
     *  outside a sane range rather than printing a nonsense m².
     * ---------------------------------------------------------------- */
    var UNIT_MM = { mm: 1, cm: 10, m: 1000 };

    function parseSize(title) {
      if (!title) return null;
      /* The first TWO figures with units, so the trailing thickness
         ("x 12.7mm") is never mistaken for a side. */
      var m = /(\d+(?:[.,]\d+)?)\s*(mm|cm|m)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*(mm|cm|m)\b/i
        .exec(title);
      if (!m) return null;

      var uw = m[2].toLowerCase();
      var ul = m[4].toLowerCase();
      var w = parseFloat(m[1].replace(',', '.')) * UNIT_MM[uw];
      var l = parseFloat(m[3].replace(',', '.')) * UNIT_MM[ul];
      /* A ground-protection tile is between 10cm and 10m a side. Anything
         else means the regex found something that was not a size. */
      if (!(w >= 100 && w <= 10000 && l >= 100 && l <= 10000)) return null;

      return {
        w: Math.round(w),
        l: Math.round(l),
        /* Shown in the units the title used — "120cm × 240cm" reads as the
           product does everywhere else on the page, where a normalised
           "1200mm × 2400mm" would not. */
        text: m[1].replace(',', '.') + uw + ' × ' + m[3].replace(',', '.') + ul
      };
    }

    var SET_W = DATA.matWidthMm;
    var SET_L = DATA.matLengthMm;
    var DERIVED = (SET_W && SET_L) ? null : parseSize(MAT && MAT.label);

    var MAT_W = SET_W || (DERIVED ? DERIVED.w : 0);
    var MAT_L = SET_L || (DERIVED ? DERIVED.l : 0);

    var SIZE_TEXT = DERIVED ? DERIVED.text
      : (MAT_W && MAT_L ? MAT_W + 'mm × ' + MAT_L + 'mm' : '');

    /* m² per unit, from the millimetre dimensions rather than a second typed
       figure — 915 × 2400 is 2.196 m², and a hand-entered 2.2 beside a
       hand-entered 915 × 2400 is a discrepancy waiting to be spotted. */
    var MAT_AREA = (MAT_W * MAT_L) / 1000000;

    /* What the thing being counted is CALLED, from a setting rather than
       written into these strings.

       It reaches eight places — three step headings, the size line, the
       stepper's aria labels, the quick-pick chips, the buybar summary and the
       headline's "per …". The product was renamed from "Standard Temporary
       Car Park Mats" to "Standard Ground Protection Tiles" during the build,
       which changed exactly this word; a second rename should not need a
       deploy. Lower case, because every use of it is mid-sentence. */
    var NOUN = DATA.unitNoun || 'tile';
    var NOUN_PL = DATA.unitNounPlural || (NOUN + 's');

    function nounFor(n) {
      return n === 1 ? NOUN : NOUN_PL;
    }

    /* Quick-pick quantities. The stepper opens at 1 — most shoppers are not
       buying one, but starting at a made-up 10 quotes an order nobody asked
       for — so these carry the common sizes in one tap. Blank setting, no
       chips. */
    var QTY_PRESETS = String(DATA.qtyPresets || '')
      .split(',')
      .map(function (v) { return parseInt(v, 10); })
      .filter(function (v) { return v > 0; });

    function resolved(part) {
      return !!(part && part.variantId && part.available && part.price !== null);
    }

    /* Only offer a lock option whose BOTH parts this product actually stocks.
       "Lay loose" has no parts and is therefore always available. A card whose
       connectors do not resolve would price as "—" and block the CTA the
       moment it was chosen, which is a dead end dressed up as a choice — the
       same rule the tile-kit build settled on for tile types and edge
       colours. */
    function partsFor(lockId) {
      return {
        dbl: CONNECTORS.filter(function (p) { return p.lockId === lockId && p.kind === 'double'; })[0] || null,
        sgl: CONNECTORS.filter(function (p) { return p.lockId === lockId && p.kind === 'single'; })[0] || null
      };
    }

    var LOCK_OPTIONS = LOCKS.filter(function (l) {
      if (l.id === 'none') return true;
      var p = partsFor(l.id);
      return resolved(p.dbl) && resolved(p.sgl);
    });

    /* ---------------------------------------------------------------- *
     *  Shapes
     *  -------
     *  `rows` is the number of units across the SHORT axis. `null` means
     *  "square it off" — the open-area case, where the row count follows
     *  from the quantity.
     *
     *  `cols`/`cellRows` are the pictogram only: a fixed 1×4 / 2×3 / 3×3
     *  diagram of the shape, not a drawing of the shopper's actual order.
     * ---------------------------------------------------------------- */
    var SHAPES = [
      { id: 'row',    label: 'Single row', sub: 'Access route or track',   rows: 1,    cellRows: 1, cellCols: 4 },
      { id: 'double', label: 'Double row', sub: 'Wide track or roadway',   rows: 2,    cellRows: 2, cellCols: 3 },
      { id: 'grid',   label: 'Open area',  sub: 'Car park or event field', rows: null, cellRows: 3, cellCols: 3 }
    ];

    function shapeById(id) {
      return SHAPES.filter(function (s) { return s.id === id; })[0] || SHAPES[0];
    }

    /* ---------------------------------------------------------------- *
     *  State
     * ---------------------------------------------------------------- */
    var state = {
      qty: Math.max(1, DATA.defaultQty || 10),
      shape: 'row',
      lock: 'boltfree',
      manualOpen: false,
      /* partId -> quantity. Seeded from the current selection the first time
         the list is opened (see `openManual`), so "pick them yourself" starts
         from what was already being quoted rather than from four zeros. */
      manual: {},
      manualSeeded: false,
      /* The shopper has answered at least one of the three questions. Until
         then the mobile buybar stays slim — see the snippet's comment on why
         the default order is not offered as a priced one-tap add. */
      touched: false,
      busy: false,
      added: false,
      error: ''
    };

    /* Open on something the product stocks. If the bolt-free pair does not
       resolve, the default `boltfree` would price as "—" before the shopper
       had touched anything. */
    if (!LOCK_OPTIONS.some(function (l) { return l.id === state.lock; })) {
      state.lock = LOCK_OPTIONS.length ? LOCK_OPTIONS[0].id : 'none';
    }

    /* ---------------------------------------------------------------- *
     *  The connector calculation
     *  -------------------------
     *  Tiles are laid as a rectangle: R rows of C tiles.
     *
     *    single row  R = 1
     *    double row  R = 2
     *    open area   R = round(sqrt(N)), never fewer than 2
     *    then        R = min(R, N),  C = ceil(N / R)
     *
     *  Connectors sit where tile CORNERS meet:
     *    doubles  join four tiles at an interior corner  (R−1)(C−1)
     *    singles  join two tiles along the outer edges   2(C−1) + 2(R−1)
     *
     *  N = 1 gives R = C = 1 and therefore no connectors at all, which is
     *  reachable now that the stepper opens at 1 — `connectorMix` captions
     *  that case rather than leaving three cards priced £0.00 in silence.
     *
     *  ⚠ The "a single joins 2 tiles, a double joins 4" convention is
     *  industry-standard for ground-protection tiles but was NOT confirmed
     *  against the COBA spec for this product — see the design handover's
     *  warning and §"Open" in HANDOVER-connector-kit.md. This function is the
     *  one place to change if the supplier says otherwise; the cards, the
     *  summary, the CTA and the cart all read their counts from it.
     * ---------------------------------------------------------------- */
    function layoutFor(qty, shapeId) {
      var shape = shapeById(shapeId);
      var rows = shape.rows === null ? Math.max(2, Math.round(Math.sqrt(qty))) : shape.rows;
      var R = Math.min(rows, qty);
      var C = Math.ceil(qty / R);
      return {
        rows: R,
        cols: C,
        doubles: (R - 1) * (C - 1),
        singles: 2 * (C - 1) + 2 * (R - 1)
      };
    }

    function lockCost(lockId, lay) {
      if (lockId === 'none') return 0;
      var p = partsFor(lockId);
      if (!resolved(p.dbl) || !resolved(p.sgl)) return null;
      return lay.doubles * p.dbl.price + lay.singles * p.sgl.price;
    }

    /* The full order, recomputed from scratch on every state change. Rows are
       what the summary prints and what the cart post sends, so the two can
       never disagree about what was bought. */
    function calc() {
      var qty = Math.max(1, state.qty);
      var lay = layoutFor(qty, state.shape);
      var rows = [];
      var priceable = resolved(MAT);

      rows.push({
        part: MAT,
        qty: qty,
        label: qty + ' × ' + (DATA.matLabel || (MAT && MAT.label) || 'Mat'),
        amount: resolved(MAT) ? MAT.price * qty : null
      });

      if (state.lock === 'custom') {
        CONNECTORS.forEach(function (p) {
          var q = state.manual[p.id] || 0;
          if (!q) return;
          if (!resolved(p)) { priceable = false; }
          rows.push({
            part: p,
            qty: q,
            label: q + ' × ' + p.label,
            amount: resolved(p) ? p.price * q : null
          });
        });
      } else if (state.lock !== 'none') {
        var p = partsFor(state.lock);
        [
          { part: p.dbl, qty: lay.doubles },
          { part: p.sgl, qty: lay.singles }
        ].forEach(function (r) {
          if (!r.qty) return;
          if (!resolved(r.part)) { priceable = false; return; }
          rows.push({
            part: r.part,
            qty: r.qty,
            label: r.qty + ' × ' + r.part.label,
            amount: r.part.price * r.qty
          });
        });
      }

      var total = 0;
      rows.forEach(function (r) { if (r.amount !== null) total += r.amount; });

      var items = 0;
      rows.forEach(function (r) { items += r.qty; });

      return {
        qty: qty,
        lay: lay,
        rows: rows,
        total: total,
        items: items,
        priceable: priceable,
        coverage: qty * MAT_AREA
      };
    }

    /* Any change to the three questions clears the "✓ Added" confirmation —
       leaving it up beside a total that has since moved reads as though the
       new configuration is already in the basket. */
    function set(patch) {
      Object.keys(patch).forEach(function (k) { state[k] = patch[k]; });
      state.added = false;
      state.error = '';
      render();
    }

    /* ---------------------------------------------------------------- *
     *  Shared pieces
     * ---------------------------------------------------------------- */
    function stepHead(no, title, hint) {
      return el('div', { class: 'fm-ck__step-head' }, [
        el('span', { class: 'fm-ck__step-no', 'aria-hidden': 'true', text: String(no) }),
        el('span', { class: 'fm-ck__step-title', text: title }),
        hint ? el('span', { class: 'fm-ck__step-hint', text: hint }) : null
      ]);
    }

    /* One stepper, two sizes. `focusKey` survives the wholesale re-render so
       a shopper holding "+" is not thrown out of the control they are using. */
    function stepper(value, opts) {
      opts = opts || {};
      var min = opts.min === undefined ? 0 : opts.min;
      return el('div', { class: 'fm-ck__stepper' + (opts.small ? ' fm-ck__stepper--sm' : '') }, [
        el('button', {
          type: 'button',
          class: 'fm-ck__step-btn',
          'aria-label': opts.decLabel || 'Decrease quantity',
          disabled: value <= min,
          'data-fm-ck-focus': opts.focusKey ? opts.focusKey + ':dec' : null,
          text: '−',
          on: { click: function () { opts.onDelta(-1); } }
        }),
        el('span', {
          class: 'fm-ck__stepper-val',
          'aria-live': 'polite',
          text: String(value)
        }),
        el('button', {
          type: 'button',
          class: 'fm-ck__step-btn',
          'aria-label': opts.incLabel || 'Increase quantity',
          'data-fm-ck-focus': opts.focusKey ? opts.focusKey + ':inc' : null,
          text: '+',
          on: { click: function () { opts.onDelta(1); } }
        })
      ]);
    }

    function priceUnit(text, suffix) {
      return el('span', { class: 'fm-ck__price' }, [
        el('span', { class: 'fm-ck__price-val', text: text }),
        el('span', { class: 'fm-ck__price-vat', text: suffix || vatSuffix() })
      ]);
    }

    function matSizeText() {
      return SIZE_TEXT ? 'Each ' + NOUN + ' is ' + SIZE_TEXT + '.' : '';
    }

    /* ---------------------------------------------------------------- *
     *  Step 1 — how many?
     * ---------------------------------------------------------------- */

    /* Quick-pick chips under the stepper. A chip SETS the quantity rather than
       adding to it, so tapping 20 after 10 gives 20, not 30 — the pill reads
       as "I want this many", and the stepper beside it is the way to add. */
    function qtyChips(c) {
      if (!QTY_PRESETS.length) return null;
      var wrap = el('div', { class: 'fm-ck__chips' });
      QTY_PRESETS.forEach(function (n) {
        var sel = c.qty === n;
        wrap.appendChild(el('button', {
          type: 'button',
          class: 'fm-ck__chip' + (sel ? ' is-selected' : ''),
          'aria-pressed': sel ? 'true' : 'false',
          'data-fm-ck-focus': 'chip:' + n,
          text: n + ' ' + nounFor(n),
          on: { click: function () { set({ qty: n, touched: true }); } }
        }));
      });
      return wrap;
    }

    function stepQty(c, no) {
      var each = resolved(MAT) ? dispMoney(MAT.price) + ' ' + vatSuffix() + ' each' : '';

      /* No size resolved — no size line and no coverage figure, rather than
         "Each tile is 0mm × 0mm" and "Covers approx. 0.0 m²". Everything else
         on the page still works: the arithmetic and every price are
         independent of the dimensions. */
      var coverage = MAT_AREA
        ? el('div', { class: 'fm-ck__qty-copy' }, [
            matSizeText(),
            el('br'),
            'Covers approx. ',
            el('b', { class: 'fm-ck__qty-cover', text: c.coverage.toFixed(1) + ' m²' })
          ])
        : el('div', { class: 'fm-ck__qty-copy' });

      return el('div', { class: 'fm-ck__step' }, [
        stepHead(no, 'How many ' + NOUN_PL + '?', each),
        el('div', { class: 'fm-ck__qty-card' }, [
          stepper(c.qty, {
            min: 1,
            decLabel: 'Decrease number of ' + NOUN_PL,
            incLabel: 'Increase number of ' + NOUN_PL,
            focusKey: 'qty',
            onDelta: function (d) { set({ qty: Math.max(1, state.qty + d), touched: true }); }
          }),
          coverage,
          priceUnit(resolved(MAT) ? dispMoney(MAT.price * c.qty) : '—')
        ]),
        qtyChips(c)
      ]);
    }

    /* ---------------------------------------------------------------- *
     *  Step 2 — what shape is the area?
     * ---------------------------------------------------------------- */
    function shapePictogram(shape, selected) {
      var grid = el('div', {
        class: 'fm-ck__picto',
        'aria-hidden': 'true',
        style: 'grid-template-columns: repeat(' + shape.cellCols + ', 16px)'
      });
      var n = shape.cellRows * shape.cellCols;
      for (var i = 0; i < n; i++) {
        grid.appendChild(el('span', {
          class: 'fm-ck__picto-cell' + (selected ? ' is-selected' : '')
        }));
      }
      return grid;
    }

    function stepShape(c, no) {
      var grid = el('div', { class: 'fm-ck__shapes', role: 'radiogroup', 'aria-label': 'What shape is the area?' });

      SHAPES.forEach(function (s) {
        var sel = state.shape === s.id;
        grid.appendChild(el('button', {
          type: 'button',
          class: 'fm-ck__shape' + (sel ? ' is-selected' : ''),
          role: 'radio',
          'aria-checked': sel ? 'true' : 'false',
          'data-fm-ck-focus': 'shape:' + s.id,
          on: { click: function () { set({ shape: s.id, touched: true }); } }
        }, [
          shapePictogram(s, sel),
          el('span', { class: 'fm-ck__shape-label', text: s.label }),
          el('span', { class: 'fm-ck__shape-sub', text: s.sub })
        ]));
      });

      return el('div', { class: 'fm-ck__step' }, [
        stepHead(no, 'What shape is the area?'),
        grid
      ]);
    }

    /* ---------------------------------------------------------------- *
     *  Step 3 — how should they lock together?
     * ---------------------------------------------------------------- */
    /* Non-breaking spaces around the "+". The design sets the two counts apart
       with a double space, and a plain run of spaces is collapsed to one by
       HTML whitespace handling — `white-space: pre` would fix it too but
       fights the `nowrap` this line needs. */
    function connectorMix(lay) {
      var parts = [];
      if (lay.doubles > 0) parts.push(lay.doubles + ' × double');
      if (lay.singles > 0) parts.push(lay.singles + ' × single');
      /* One unit has no joins, so both connector options cost £0.00 and the
         three cards would otherwise sit there identically priced with nothing
         saying why. Only reachable at quantity 1 — two in a row already needs
         two singles. */
      if (!parts.length) return 'Not needed for a single ' + NOUN;
      return parts.join('\u00A0\u00A0+\u00A0\u00A0');
    }

    function lockCard(lock, c) {
      var sel = state.lock === lock.id;
      var cost = lockCost(lock.id, c.lay);

      var priceText;
      if (lock.id === 'none') {
        priceText = dispMoney(0);
      } else if (cost === null) {
        priceText = '—';
      } else {
        priceText = '+ ' + dispMoney(cost);
      }

      return el('button', {
        type: 'button',
        class: 'fm-ck__lock' + (sel ? ' is-selected' : ''),
        role: 'radio',
        'aria-checked': sel ? 'true' : 'false',
        'data-fm-ck-focus': 'lock:' + lock.id,
        on: { click: function () { set({ lock: lock.id, touched: true }); } }
      }, [
        el('span', { class: 'fm-ck__lock-dot', 'aria-hidden': 'true' }),
        el('span', { class: 'fm-ck__lock-copy' }, [
          el('span', { class: 'fm-ck__lock-title', text: lock.title }),
          el('span', { class: 'fm-ck__lock-sub', text: lock.sub })
        ]),
        el('span', { class: 'fm-ck__lock-meta' }, [
          el('span', { class: 'fm-ck__lock-price', text: priceText }),
          lock.id === 'none'
            ? null
            : el('span', { class: 'fm-ck__lock-count', text: connectorMix(c.lay) })
        ])
      ]);
    }

    /* Seed the manual list from what is currently being quoted, the first time
       it is opened.

       The prototype started all four at zero and switched to custom mode on
       the first nudge, which threw away the computed mix the shopper was
       looking at a second earlier: choosing bolt-free, opening the list and
       tapping "+" once left an order of 1 connector. "Prefer to pick
       connectors yourself?" reads as "let me adjust these", so it starts from
       these. Tapping a lock card still returns to the automatic count. */
    function openManual() {
      if (!state.manualSeeded) {
        var lay = layoutFor(Math.max(1, state.qty), state.shape);
        var seed = {};
        CONNECTORS.forEach(function (p) { seed[p.id] = 0; });
        if (state.lock !== 'none' && state.lock !== 'custom') {
          var p = partsFor(state.lock);
          if (p.dbl) seed[p.dbl.id] = lay.doubles;
          if (p.sgl) seed[p.sgl.id] = lay.singles;
        }
        state.manual = seed;
        state.manualSeeded = true;
      }
      set({ manualOpen: true });
    }

    function manualRow(part) {
      var qty = state.manual[part.id] || 0;

      return el('div', { class: 'fm-ck__manual-row' + (qty > 0 ? ' is-selected' : '') }, [
        el('span', { class: 'fm-ck__manual-thumb' }, [
          part.image
            ? el('img', { src: part.image, alt: '', width: '46', height: '46' })
            : null
        ]),
        el('span', { class: 'fm-ck__manual-copy' }, [
          el('span', { class: 'fm-ck__manual-label', text: part.label }),
          el('span', { class: 'fm-ck__manual-price' }, [
            el('span', { class: 'fm-ck__manual-each', text: resolved(part) ? dispMoney(part.price) : '—' }),
            el('span', { class: 'fm-ck__manual-vat', text: vatSuffix() + ' each' })
          ])
        ]),
        stepper(qty, {
          min: 0,
          small: true,
          decLabel: 'Decrease ' + part.label,
          incLabel: 'Increase ' + part.label,
          focusKey: 'manual:' + part.id,
          onDelta: function (d) {
            var next = {};
            Object.keys(state.manual).forEach(function (k) { next[k] = state.manual[k]; });
            next[part.id] = Math.max(0, (next[part.id] || 0) + d);
            /* Editing any manual quantity IS the switch into custom mode —
               the lock cards deselect and the summary follows these numbers. */
            set({ manual: next, lock: 'custom', touched: true });
          }
        })
      ]);
    }

    function stepLock(c, no) {
      var cards = el('div', { class: 'fm-ck__locks', role: 'radiogroup', 'aria-label': 'How should the ' + NOUN_PL + ' lock together?' });
      LOCK_OPTIONS.forEach(function (l) { cards.appendChild(lockCard(l, c)); });

      var kids = [
        stepHead(no, 'How should the ' + NOUN_PL + ' lock together?'),
        cards,
        el('button', {
          type: 'button',
          class: 'fm-ck__manual-toggle',
          'aria-expanded': state.manualOpen ? 'true' : 'false',
          'data-fm-ck-focus': 'manual-toggle',
          text: state.manualOpen ? 'Hide connector list' : 'Prefer to pick connectors yourself?',
          on: {
            click: function () {
              if (state.manualOpen) { set({ manualOpen: false }); } else { openManual(); }
            }
          }
        })
      ];

      if (state.manualOpen) {
        var list = el('div', { class: 'fm-ck__manual' });
        CONNECTORS.forEach(function (p) { list.appendChild(manualRow(p)); });
        kids.push(list);
      }

      return el('div', { class: 'fm-ck__step fm-ck__step--lock' }, kids);
    }

    /* ---------------------------------------------------------------- *
     *  Summary, CTA, footnote
     * ---------------------------------------------------------------- */
    function summaryBox(c) {
      var lines = el('div', { class: 'fm-ck__sum-lines' });
      c.rows.forEach(function (r) {
        lines.appendChild(el('div', { class: 'fm-ck__sum-line' }, [
          el('span', { class: 'fm-ck__sum-label', text: r.label }),
          el('span', { class: 'fm-ck__sum-amount', text: r.amount === null ? '—' : dispMoney(r.amount) })
        ]));
      });

      return el('div', { class: 'fm-ck__summary' }, [
        lines,
        el('div', { class: 'fm-ck__sum-total' }, [
          el('span', { text: 'Order total' }),
          el('span', { class: 'fm-ck__sum-total-value' }, [
            c.priceable ? dispMoney(c.total) : '—',
            ' ',
            el('span', { class: 'fm-ck__sum-total-vat', text: vatSuffix() })
          ])
        ])
      ]);
    }

    function canAdd(c) {
      if (state.busy) return false;
      if (!c.priceable) return false;
      return c.rows.some(function (r) { return r.qty > 0 && r.part && r.part.variantId; });
    }

    /* The CTA carries the total ex VAT whatever the page's display mode — the
       sitewide rule the tile kit follows too. `withPrice` is false only where
       the button is too narrow to hold it. */
    function ctaLabel(c, withPrice) {
      if (state.busy) return 'Adding to basket…';
      if (state.added) return '✓ Added to basket';
      if (!c.priceable) return 'Call us to order';
      if (!withPrice) return 'Add all to Basket';
      return 'Add all to Basket — ' + money(c.total) + ' + VAT';
    }

    function footnote() {
      var phone = DATA.phone || '0121 702 1659';
      return el('div', { class: 'fm-ck__footnote' }, [
        (DATA.footnote || 'Connector counts are a guide based on your layout — unsure? Call us on') + ' ',
        el('a', {
          class: 'fm-ck__tel',
          href: 'tel:' + phone.replace(/[^0-9+]/g, ''),
          text: phone
        }),
        '.'
      ]);
    }

    function ctaButton(c, withPrice) {
      return el('button', {
        type: 'button',
        class: 'fm-ck__cta' + (state.added ? ' is-added' : '') + (state.busy ? ' is-busy' : ''),
        disabled: !canAdd(c) && !state.added,
        'data-fm-ck-focus': 'cta',
        text: ctaLabel(c, withPrice),
        on: { click: function () { addToCart(c); } }
      });
    }

    /* Every part that failed to resolve, named. An unresolved part is a
       matcher that needs changing in the theme editor, and "—" on its own
       sends whoever notices it hunting. */
    function unresolvedNotice() {
      var missing = [];
      if (!resolved(MAT)) missing.push('the mat');
      CONNECTORS.forEach(function (p) {
        if (!resolved(p) && state.lock !== 'none') {
          if (state.lock === 'custom' ? (state.manual[p.id] || 0) > 0 : p.lockId === state.lock) {
            missing.push(p.label);
          }
        }
      });
      if (!missing.length) return null;
      return el('div', { class: 'fm-ck__error', role: 'status' }, [
        'We could not price ' + missing.join(', ') + ' — please call us on ',
        el('a', { class: 'fm-ck__tel', href: 'tel:' + (DATA.phone || '').replace(/[^0-9+]/g, ''), text: DATA.phone || '' }),
        '.'
      ]);
    }

    /* ---------------------------------------------------------------- *
     *  Render
     * ---------------------------------------------------------------- */
    function renderControls(surface, c) {
      var frag = document.createDocumentFragment();

      frag.appendChild(stepQty(c, 1));
      frag.appendChild(stepShape(c, 2));
      frag.appendChild(stepLock(c, 3));
      frag.appendChild(summaryBox(c));

      var notice = unresolvedNotice();
      if (notice) frag.appendChild(notice);

      /* Desktop carries the CTA inline; the sheet pins it in the footer, so
         adding it here too would put two Add buttons on one screen. */
      if (surface === 'desktop') frag.appendChild(ctaButton(c, true));

      if (state.error) {
        frag.appendChild(el('div', { class: 'fm-ck__error', role: 'status', text: state.error }));
      }

      frag.appendChild(footnote());
      return frag;
    }

    function renderBar(c) {
      if (!bar) return;
      bar.setAttribute('data-mode', state.touched ? 'selected' : 'slim');

      var summary = bar.querySelector('[data-fm-ck-bar-summary]');
      var total = bar.querySelector('[data-fm-ck-bar-total]');
      var cta = bar.querySelector('[data-fm-ck-bar-cta]');

      if (summary) {
        clear(summary);
        var lockName = state.lock === 'custom'
          ? 'your connectors'
          : (LOCKS.filter(function (l) { return l.id === state.lock; })[0] || {}).title || '';
        append(summary, [
          c.qty + ' ' + nounFor(c.qty) + ' · ' + lockName.toLowerCase() + ' · ',
          el('span', { class: 'fm-ck-buybar__change', text: 'Change' })
        ]);
      }
      if (total) total.textContent = c.priceable ? dispMoney(c.total) : '—';
      if (cta) {
        cta.textContent = ctaLabel(c, false);
        cta.disabled = !canAdd(c) && !state.added;
        cta.classList.toggle('is-added', state.added);
        cta.classList.toggle('is-busy', state.busy);
      }
      measureBar();
    }

    function renderSheet(c) {
      if (!sheetBody) return;

      /* Hold the scroll position across the rebuild. The body is cleared and
         re-appended on every state change, which resets scrollTop to 0 — so
         nudging a connector quantity threw the shopper back up to step 1, on
         the one surface where the sheet is the whole screen. Restored
         synchronously, before paint, so there is no visible jump. */
      var keepScroll = sheetBody.scrollTop;

      clear(sheetBody);
      /* Only build the sheet's copy on mobile. Building both surfaces every
         render doubles the DOM and duplicates every `data-fm-ck-focus` key on
         the page — and the focus restore then targets whichever copy comes
         first, which is the hidden desktop mount. */
      if (isMobile()) sheetBody.appendChild(renderControls('sheet', c));
      /* Clamped: the rebuilt content can be shorter than what it replaced
         (closing the manual list drops four rows), and an out-of-range
         scrollTop silently becomes 0 — the very jump this prevents. */
      sheetBody.scrollTop = Math.min(keepScroll, Math.max(0, sheetBody.scrollHeight - sheetBody.clientHeight));

      if (!sheetFooter) return;
      var items = sheetFooter.querySelector('[data-fm-ck-sheet-items]');
      var total = sheetFooter.querySelector('[data-fm-ck-sheet-total]');
      var cta = sheetFooter.querySelector('[data-fm-ck-sheet-cta]');
      if (items) items.textContent = 'Order total · ' + c.items + ' items';
      if (total) total.textContent = c.priceable ? dispMoney(c.total) : '—';
      if (cta) {
        cta.textContent = ctaLabel(c, false);
        cta.disabled = !canAdd(c) && !state.added;
        cta.classList.toggle('is-added', state.added);
        cta.classList.toggle('is-busy', state.busy);
      }
    }

    /* A wholesale re-render is the only way to keep the surfaces honest, but
       it destroys the node the shopper is using. `data-fm-ck-focus` keys are
       derived from state, not node identity, so they survive the rebuild.

       Scoped to the surface focus was in: the keys are only unique WITHIN a
       surface, and restoring globally would hand focus to whichever copy sits
       first in the DOM. */
    function surfaceOf(node) {
      while (node && node !== document.body) {
        if (node === controlsMount || node === sheetBody) return node;
        node = node.parentNode;
      }
      return null;
    }

    function captureFocus() {
      var a = document.activeElement;
      if (!a || !a.getAttribute) return null;
      var key = a.getAttribute('data-fm-ck-focus');
      if (!key) return null;
      return { key: key, root: surfaceOf(a) };
    }

    function restoreFocus(snap) {
      if (!snap) return;
      var root = snap.root && snap.root.isConnected ? snap.root : document;
      var next = root.querySelector('[data-fm-ck-focus="' + snap.key + '"]');
      if (!next || next.disabled) return;
      next.focus({ preventScroll: true });
    }

    function render() {
      var c = calc();
      var snap = captureFocus();

      clear(controlsMount);
      if (!isMobile()) controlsMount.appendChild(renderControls('desktop', c));

      renderSheet(c);
      renderBar(c);
      restoreFocus(snap);
    }

    /* ---------------------------------------------------------------- *
     *  Cart
     * ---------------------------------------------------------------- */
    function orderId() {
      return 'CK-' + Date.now().toString(36).toUpperCase() + '-' +
        Math.random().toString(36).slice(2, 6).toUpperCase();
    }

    function layoutProperty(c) {
      var shape = shapeById(state.shape);
      return shape.label + ' — ' + c.lay.rows + ' × ' + c.lay.cols +
        ' (' + c.coverage.toFixed(1) + ' m²)';
    }

    function addToCart(c) {
      if (!canAdd(c)) return;

      var id = orderId();
      var items = [];
      c.rows.forEach(function (r) {
        if (!r.qty || !r.part || !r.part.variantId) return;
        /* Underscore-prefixed keys are hidden from the shopper but travel
           through to the order, which is what ties the lines together in
           fulfilment — the same contract as `_kit_id` on the tile kit. */
        var props = { _ck_id: id };
        /* Layout only on the connector lines. On a mat line it reads as if
           that one mat were the layout. */
        if (r.part.role === 'connector' && state.lock !== 'custom') {
          props.Layout = layoutProperty(c);
        }
        items.push({ id: r.part.variantId, quantity: r.qty, properties: props });
      });
      if (!items.length) return;

      state.busy = true;
      state.error = '';
      render();

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        if (!res.ok) throw new Error('add failed');
        return res.json();
      }).then(function () {
        state.added = true;
        render();
        if (typeof window.addToCartAndOpenDrawer === 'function') {
          window.addToCartAndOpenDrawer();
          settle();
        } else {
          window.location.href = '/cart';
        }
      }).catch(function () {
        state.busy = false;
        state.error = 'Sorry — that could not be added. Please try again.';
        render();
      });
    }

    /* The drawer's `.active` class is the only reliable signal that the add
       landed and the shopper can see it. Until then the CTAs stay disabled and
       say "Adding to basket…" — an optimistic reset re-enables the button
       mid-flight, which is the spam-click window the options sheet hit. */
    function whenDrawerOpens(onOpen, onTimeout) {
      var waited = 0;
      var timer = setInterval(function () {
        if (document.querySelector('cart-drawer.active, #CartDrawer.active')) {
          clearInterval(timer);
          onOpen();
          return;
        }
        waited += 150;
        if (waited >= 10000) {
          clearInterval(timer);
          onTimeout();
        }
      }, 150);
    }

    /* The configuration SURVIVES the add, for the same reason the tile kit's
       does: the shopper has answered three questions, and wiping that leaves
       them on a reset form with a greyed-out button, which reads as broken
       rather than as finished. "✓ Added" holds for a moment, then the CTA
       returns to normal; any change to the three steps clears it earlier. */
    var addedTimer = null;
    var ADDED_HOLD_MS = 2500;

    function settle() {
      whenDrawerOpens(function () {
        state.busy = false;
        state.added = true;
        closeSheet();
        render();
        clearTimeout(addedTimer);
        addedTimer = setTimeout(function () {
          state.added = false;
          render();
        }, ADDED_HOLD_MS);
      }, function () {
        state.busy = false;
        render();
      });
    }

    /* ---------------------------------------------------------------- *
     *  Sheet plumbing — the machinery `fm-tile-kit.js` settled on
     * ---------------------------------------------------------------- */
    var lastFocus = null;
    var savedScrollY = 0;

    function isOpen() {
      return !!sheet && sheet.getAttribute('data-state') === 'open';
    }

    function openSheet() {
      if (!sheet || !isMobile() || isOpen()) return;
      lastFocus = document.activeElement;
      savedScrollY = window.scrollY || window.pageYOffset || 0;

      sheet.removeAttribute('inert');
      sheet.setAttribute('aria-hidden', 'false');
      sheet.setAttribute('data-state', 'open');
      document.body.classList.add('fm-ck-sheet-open');
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';

      var closeBtn = sheet.querySelector('.fm-ck-sheet__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
      document.addEventListener('keydown', onKeydown);
      syncPanelHeight();
    }

    function closeSheet() {
      if (!sheet || !isOpen()) return;
      sheet.setAttribute('data-state', 'closed');
      sheet.setAttribute('aria-hidden', 'true');
      sheet.setAttribute('inert', '');
      document.body.classList.remove('fm-ck-sheet-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, savedScrollY);
      document.removeEventListener('keydown', onKeydown);
      syncPanelHeight();
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }
    }

    /* Keep the panel inside the VISUAL viewport. `max-height: 93%` resolves
       against the layout viewport, which does not shrink when the software
       keyboard opens — and `dvh` tracks browser chrome, not the keyboard.
       visualViewport is the only thing that reports both. */
    function syncPanelHeight() {
      var panel = sheet && sheet.querySelector('.fm-ck-sheet__panel');
      if (!panel) return;
      if (!isOpen() || !window.visualViewport) {
        panel.style.maxHeight = '';
        panel.style.transform = '';
        return;
      }
      var vv = window.visualViewport;
      var hidden = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      panel.style.maxHeight = Math.round(vv.height * 0.93) + 'px';
      panel.style.transform = hidden ? 'translateY(-' + hidden + 'px)' : '';
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', syncPanelHeight);
      window.visualViewport.addEventListener('scroll', syncPanelHeight);
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        e.preventDefault();
        closeSheet();
        return;
      }
      if (e.key === 'Tab' || e.keyCode === 9) trapFocus(e);
    }

    function trapFocus(e) {
      var panel = sheet && sheet.querySelector('.fm-ck-sheet__panel');
      if (!panel) return;
      var focusables = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
        ' select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.querySelectorAll('[data-fm-ck-open]').forEach(function (n) {
      n.addEventListener('click', openSheet);
    });
    if (sheet) {
      sheet.querySelectorAll('[data-fm-ck-close]').forEach(function (n) {
        n.addEventListener('click', closeSheet);
      });
    }
    if (bar) {
      var barCta = bar.querySelector('[data-fm-ck-bar-cta]');
      if (barCta) barCta.addEventListener('click', function () { addToCart(calc()); });
    }
    if (sheetFooter) {
      var sheetCta = sheetFooter.querySelector('[data-fm-ck-sheet-cta]');
      if (sheetCta) sheetCta.addEventListener('click', function () { addToCart(calc()); });
    }

    /* Leaving the mobile breakpoint with the sheet open (rotation, a tablet
       resize) hides the panel via CSS but would strand the body scroll lock. */
    window.addEventListener('resize', function () {
      if (!isMobile() && isOpen()) closeSheet();
    });

    /* The footer's height changes between its two modes, so the page's bottom
       padding is measured rather than guessed. */
    function measureBar() {
      if (!bar || !isMobile()) return;
      var h = bar.getBoundingClientRect().height;
      if (h > 0) document.documentElement.style.setProperty('--fm-ck-buybar-height', h + 'px');
    }
    if (window.ResizeObserver && bar) {
      try { new ResizeObserver(measureBar).observe(bar); } catch (e) { /* resize listener covers it */ }
    }
    window.addEventListener('resize', measureBar);

    /* Only one surface renders at a time, so crossing the breakpoint has to
       rebuild rather than just restyle. */
    var wasMobile = isMobile();
    window.addEventListener('resize', function () {
      if (isMobile() !== wasMobile) {
        wasMobile = isMobile();
        render();
      }
    });

    /* fm-vat.js owns the display mode for the whole page. This component
       renders its prices from data rather than from [data-ex-price]
       attributes, so it re-renders on the event — one source of truth. */
    document.addEventListener('fm:vat-changed', render);

    /* ---- init ---- */
    if (bar) bar.removeAttribute('hidden');
    document.body.classList.add('fm-ck-active');
    render();
    measureBar();
  });
})();
